import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AutoCompleteSearch from './AutoCompleteSearch';
import ButtonAction from './buttons/ButtonAction';
import { AddIcon } from '../components/buttons/IconButtons';
import { CustomTextField } from './CustomTextField';
import CustomMediumChip from './CustomMediumChip';
import {filterByLabelEmailPhone, formatTimeUnit} from '../Apis/functions';

/**
 * @author Susana Garcia Rodriguez
 * @jobTitle Frontend Developer
 * @email sgarcia@ibl.mx
 *
 * @description CustomAddMediumChip is a reusable SubComponent that allows users to select items
 *              from an autocomplete list, specify a numeric unit for each item, and add them
 *              to a list rendered as removable chips.
 * @param {String} id: Identifier for the CustomTextField (required)
 * @param {string} autocompleteId - ID for the AutoCompleteSearch input (required).
 * @param {string} buttonId - ID for the Add button (required).
 * @param {boolean} emptyFields - Flag to highlight the field as empty (optional).
 * @param {boolean} disabled - Disables the input, unit field, and button (optional).
 * @param {string} label - Translation key for the autocomplete input label (required).
 * @param {boolean} required - Whether the inputs are required (optional).
 * @param {Array} data - Array of objects to choose from; each must have `id` and `label` (required).
 * @param {Array} selectedList - Array of currently selected items shown as chips (required).
 * @param {Function} setSelectedList - Function to update the `selectedList` state (required).
 * @param {string} transUnit - Translation key for the unit input label (required).
 * @param {string} typeUnit - Type of unit to show units on chip.
 * @param {string} unitLength - Lenght of unit input.
 * @param {JSX.Element|false} children - Optional additional content to render below the title.
 * @returns {JSX.Element}
 */
export const CustomAddMediumChip = ({
    autocompleteId,
    buttonId,
    emptyFields,
    disabled,
    label,
    required,
    data,
    selectedList,
    setSelectedList,
    transUnit,
    id,
    unit,
    onDeleteItem = null,
    typeUnit = 'quantity',
    unitLength = 4,
    onSelectItem,
    onChangeUnit
}) => {
    const { t } = useTranslation();
    const [itemSelected, setItemSelected] = useState(null);
    const [number, setNumber] = useState('');
    const [errorUnit, setErrorUnit] = useState(false);
    const [emptyFieldsAux, setEmptyFieldsAux] = useState(false);
    const handleSelect = (val) => {
        setItemSelected(val);
        onSelectItem?.(val);
    };

    const handleChangeNumber = (val) => {
        setNumber(val);
        onChangeUnit?.(val);
    };
    const handleDelete =  async (element) => {
        if (onDeleteItem) {
            await onDeleteItem(element);
        }
        const aux = [...selectedList];
        setSelectedList(aux.filter((e) => e.id !== element?.id));
    };

    const handleAdd = () => {
        if (!itemSelected || number === '') {
            setErrorUnit(true);
            return;
        }
        setErrorUnit(false);
        if(!itemSelected || !number){
            setEmptyFieldsAux(true);
            return;
        }
        setEmptyFieldsAux(false);

        let displayUnit;
        if (typeUnit === 'time') {
            displayUnit = formatTimeUnit(number);
        } else {
            displayUnit = number;
        }
        const itemWithUnit = {
            ...itemSelected,
            unit: `${displayUnit} ${t(unit)}`,
                time: number
        };
        setSelectedList([...selectedList, itemWithUnit]);
        setItemSelected(null);
        setNumber('');
        onSelectItem?.(null);
        onChangeUnit?.('');
    };

    return (
        <>
            <div className='d-flex align-items-start flex-wrap mb-3 mb-sm-0'>
                <div  className='flex-grow-1'>
                    <AutoCompleteSearch
                        id={autocompleteId}
                        data={data.filter((e) => !selectedList.map((e) => e.id).includes(e.id))}
                        value={itemSelected?.label || ''}
                         setValue={handleSelect}
                        label={t(label)}
                        required={required || (!itemSelected && number)}
                        emptyFields={emptyFields || emptyFieldsAux}
                        disabled={disabled}
                        handleFn={setItemSelected}
                        reset={false}
                        filterOptions={filterByLabelEmailPhone}
                    />
                </div>

                <div >
                    <CustomTextField
                       id={id}
                        name='unit'
                        trans={t(transUnit)}
                        type='number'
                        multiline={false}
                        value={number}
                        length={unitLength}
                        required={required || (itemSelected && !number)}
                        emptyFields={errorUnit || emptyFields || emptyFieldsAux}
                        disabled={disabled}
                        showCounter={true}
                        onChange={(e) => handleChangeNumber(e.target.value)}
                    />
                </div>
                <div>
                    <ButtonAction
                        id={buttonId}
                        name='add'
                        className='btn-secondary-600'
                        Icon={AddIcon}
                        splitName={false}
                        onClick={handleAdd}
                        disabled={disabled || typeof itemSelected?.id === "undefined"}
                    />
                </div>
            </div>

            {selectedList?.length > 0 && (
                <div className='d-flex gap-4 input-padding-bottom flex-wrap'>
                    {selectedList.map((element) => (
                        <CustomMediumChip
                            key={element.id}
                            title={element.label}
                            disabled={disabled}
                            onClickClose={() => handleDelete(element)}
                        >
                            <div className='mt-2 d-flex w-100 align-items-center align-content-center justify-content-between'>
                                {element.unit && (
                                    <span>
                                          {`${element.unit || ''} ${t(element.subtitle || '').toLowerCase()}`}
                                    </span>
                                )}
                            </div>
                        </CustomMediumChip>
                    ))}
                </div>
            )}
        </>
    );
};
